import { useCallback, useEffect, useMemo } from 'react'

import { Box, Button, HStack, HoverCard, Icon, Portal, StackSeparator, Text, useDisclosure } from '@chakra-ui/react'
import {
  CubeBackIcon,
  CubeDownIcon,
  CubeFrontIcon,
  CubeLeftIcon,
  CubeRightIcon,
  CubeUpIcon,
  ToolbarCameraIcon,
} from '@editor/assets/icons'
import { WarningIcon } from '@editor/chakra-ui-icons'
import { EDITOR_TOOLS, EDITOR_TOOL_BUTTON_SIZE } from '@editor/config/constants'
import { CameraViewOrientation } from '@editor/interfaces/camera'
import { InspectionArea } from '@editor/interfaces/inspection'
import ToolbarButton from '@editor/pages/projects/editor/toolbar/components/ToolbarButton'
import CameraFocusButton from '@editor/pages/projects/editor/tools/CameraFocus/components/CameraFocusButton'
import CameraViewButton from '@editor/pages/projects/editor/tools/CameraProfile/components/CameraViewButton'
import { RootState } from '@editor/store/app'
import { cloneDeep } from 'lodash'
import { BsGearFill } from 'react-icons/bs'
import { useSelector } from 'react-redux'
import { Link } from 'react-router'
import { Matrix4, PerspectiveCamera, Quaternion, Vector3 } from 'three'
import { ArcballControls } from 'three-stdlib'
import { ArcballControls as ThreeArcballControls } from 'three/addons/controls/ArcballControls.js'

/**
 * Timeout for the camera profile sub-toolbar to close.
 * Value is in milliseconds.
 */
const CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT = 3 * 1000

/**
 * Camera sub-toolbar timeout.
 */
let subToolbarTimeout: NodeJS.Timeout

const CameraToolbar = ({
  isAllowedToModify = false,
  disableEditCameraProfile = false,
  isEditing = false,
  selectedTool = '',
  inspectionArea,
  controls,
  camera,
  onChangeTool,
}: {
  /**
   * Permissions to modify the camera profile.
   */
  isAllowedToModify: boolean

  /**
   * Whether the feature to edit camera profile is enabled regardless of permissions.
   * If it is disabled, a message will pop up and say user has to visit 3DGUI to edit it.
   */
  disableEditCameraProfile?: boolean

  /**
   * Whether the camera profile is being edited.
   */
  isEditing?: boolean

  /**
   * Inspection area to be used.
   * Defaults to the one set in page store.
   */
  inspectionArea?: InspectionArea

  selectedTool?: string
  controls: ArcballControls | ThreeArcballControls
  camera: PerspectiveCamera
  onChangeTool: (tool: string) => void
}) => {
  // Popover toolbar
  const { open, onOpen, onClose } = useDisclosure()

  // Store
  const pageArea = useSelector((state: RootState) => state.page.inspectionArea)
  const area = useMemo(() => inspectionArea || pageArea, [inspectionArea, pageArea])
  const cameraProfile = useMemo(() => area?.camera_profile, [area])

  /**
   * Re-orient camera to saved profile.
   * @param orientation Camera view orientation.
   * @param noAutoClose Flag to not auto close the camera profile sub-toolbar.
   */
  const orientCameraToProfile = useCallback(
    (orientation: CameraViewOrientation, noAutoClose?: boolean) => {
      if (cameraProfile && controls && camera) {
        const state = cloneDeep(cameraProfile.state)

        if (orientation !== CameraViewOrientation.FRONT) {
          const position = new Vector3()
          const quaternion = new Quaternion()
          const scale = new Vector3()
          new Matrix4().fromArray(state.arcballState.cameraMatrix.elements).decompose(position, quaternion, scale)

          // calculate normal of target to camera position
          const normal = new Vector3(...cameraProfile.positions.front)
            .sub(new Vector3(...cameraProfile.target))
            .normalize()

          if (orientation === CameraViewOrientation.TOP) {
            state.arcballState.cameraUp = { ...normal }
          } else if (orientation === CameraViewOrientation.BOTTOM) {
            // invert normal
            const invertedNormal = normal.clone().multiplyScalar(-1)
            state.arcballState.cameraUp = { ...invertedNormal }
          }

          const updatedMatrix = new Matrix4().compose(
            new Vector3(...cameraProfile.positions[orientation]),
            quaternion,
            scale,
          )
          state.arcballState.cameraMatrix.elements = updatedMatrix.toArray()
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
        ;(controls as any).setStateFromJSON(JSON.stringify(state))
      }
      if (!noAutoClose) onClose()
    },
    [cameraProfile, camera, controls, onClose],
  )

  /**
   * Clicking the main view button will orient the camera to the front view
   * as well as open the camera profile sub-toolbar.
   */
  const onClickMainView = useCallback(() => {
    onOpen()
    orientCameraToProfile(CameraViewOrientation.FRONT, true)

    clearTimeout(subToolbarTimeout)
    subToolbarTimeout = setTimeout(() => {
      onClose()
    }, CAMERA_PROFILE_SUB_TOOLBAR_TIMEOUT)
  }, [onOpen, onClose, orientCameraToProfile])

  /**
   * Close sub-toolbar when the selected tool is camera profile.
   */
  useEffect(() => {
    if (isEditing) {
      onClose()
    }
  }, [isEditing, onClose])

  return (
    <HStack
      id="camera-toolbar"
      bottom={2}
      left={2}
      h={EDITOR_TOOL_BUTTON_SIZE}
      position="absolute"
      backgroundColor="gray2.800"
      borderRadius="md"
      gap={0}
      data-testid="camera-assistant-bar"
      pointerEvents="auto"
      separator={<StackSeparator borderColor="whiteAlpha.200" />}
    >
      <CameraFocusButton selectedTool={selectedTool} changeTool={onChangeTool} />

      <HoverCard.Root open={disableEditCameraProfile ? undefined : false}>
        <HoverCard.Trigger asChild>
          <Box>
            {isAllowedToModify && (!cameraProfile || isEditing) ? (
              <ToolbarButton
                icon={
                  <Box position="relative">
                    <ToolbarCameraIcon />
                    <>
                      <Icon position="absolute" top="-2px" right="-2px" width="75%" height="75%" color="black" asChild>
                        <BsGearFill />
                      </Icon>
                      <Icon position="absolute" top="-2px" right="-2px" width="65%" height="65%" color="white" asChild>
                        <BsGearFill />
                      </Icon>
                      {!cameraProfile && (
                        <Icon
                          position="absolute"
                          top="-60%"
                          right="-60%"
                          width="16px"
                          height="16px"
                          color="orange"
                          asChild
                        >
                          <WarningIcon />
                        </Icon>
                      )}
                    </>
                  </Box>
                }
                label={disableEditCameraProfile ? undefined : '正面を設定'}
                toolType={EDITOR_TOOLS.CAMERA_PROFILE}
                buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                selectedTool={selectedTool}
                changeTool={onChangeTool}
                toolTipPlacement="top"
              />
            ) : null}
          </Box>
        </HoverCard.Trigger>
        <Portal>
          <HoverCard.Positioner>
            <HoverCard.Content
              width="fit-content"
              textAlign="center"
              backgroundColor="gray2.800"
              color="white"
              borderColor="gray2.900"
            >
              <HoverCard.Arrow
                bg="gray2.800"
                borderColor="gray2.800"
                style={
                  {
                    '--popper-arrow-shadow-color': '{colors.gray2.800}',
                  } as React.CSSProperties
                }
              />
              <HoverCard.Content>
                <Text>正面の設定は3D画面でしてください</Text>
                <Button colorPalette="blue" size="sm" mt={3} asChild>
                  <Link
                    to={`/projects/${area?.project_id}/editor?area=${area?.inspection_area_id}#tool-${EDITOR_TOOLS.CAMERA_PROFILE}`}
                  >
                    3D画面へ
                  </Link>
                </Button>
              </HoverCard.Content>
            </HoverCard.Content>
          </HoverCard.Positioner>
        </Portal>
      </HoverCard.Root>

      {cameraProfile && !isEditing ? (
        <Box w={EDITOR_TOOL_BUTTON_SIZE} h={EDITOR_TOOL_BUTTON_SIZE} position="relative">
          <CameraViewButton
            label="カメラを正面(-y)からの視点に移動"
            icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
            onClick={onClickMainView}
            onLongPress={onOpen}
            hasSubtoolbar
          />
          <Box position="relative" bottom="calc(100% + 4px)">
            <HoverCard.Root
              defaultOpen
              open={open}
              onOpenChange={(e) => {
                if (!e.open) {
                  onClose()
                }
              }}
              positioning={{
                placement: 'top',
              }}
            >
              <HoverCard.Positioner>
                <HoverCard.Content
                  h={EDITOR_TOOL_BUTTON_SIZE}
                  w="auto"
                  borderWidth={0}
                  backgroundColor="transparent"
                  bottom={EDITOR_TOOL_BUTTON_SIZE}
                  marginBottom={1}
                >
                  <HoverCard.Arrow
                    w="8px"
                    h="8px"
                    top={EDITOR_TOOL_BUTTON_SIZE}
                    left="12px"
                    transform="rotate(45deg) translate(0, -5px)"
                    backgroundColor="gray2.800"
                    boxShadow="1px 1px 1px 0 {colors.whiteAlpha.500}"
                    position="absolute"
                  />
                  <HoverCard.Content
                    p={0}
                    backgroundColor="secondary.800"
                    borderColor="secondary.700"
                    borderWidth={1}
                    borderRadius="md"
                  >
                    <HStack
                      h={EDITOR_TOOL_BUTTON_SIZE}
                      backgroundColor="gray2.800"
                      borderRadius="md"
                      gap={0}
                      data-testid="camera-profile-view-orientation-bar"
                      separator={<StackSeparator borderColor="whiteAlpha.200" />}
                    >
                      <CameraViewButton
                        label="カメラを左(-x)からの視点に移動"
                        icon={<CubeLeftIcon width="50%" height="50%" data-testid="cameraFromLeft" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.LEFT)}
                      />

                      <CameraViewButton
                        label="カメラを右(+x)からの視点に移動"
                        icon={<CubeRightIcon width="50%" height="50%" data-testid="cameraFromRight" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.RIGHT)}
                      />
                      <CameraViewButton
                        label="カメラを正面(-y)からの視点に移動"
                        icon={<CubeFrontIcon width="50%" height="50%" data-testid="cameraFromFront" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.FRONT)}
                      />
                      <CameraViewButton
                        label="カメラを裏(+y)からの視点に移動"
                        icon={<CubeBackIcon width="50%" height="50%" data-testid="cameraFromBack" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.BACK)}
                      />
                      <CameraViewButton
                        label="カメラを下(-z)からの視点に移動"
                        icon={<CubeDownIcon width="50%" height="50%" data-testid="cameraFromBottom" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.BOTTOM)}
                      />
                      <CameraViewButton
                        label="カメラを上(+z)からの視点に移動"
                        icon={<CubeUpIcon width="50%" height="50%" data-testid="cameraFromTop" />}
                        onClick={() => orientCameraToProfile(CameraViewOrientation.TOP)}
                      />
                      {isAllowedToModify && (
                        <ToolbarButton
                          icon={
                            <Box position="relative">
                              <ToolbarCameraIcon />
                              <>
                                <Icon
                                  position="absolute"
                                  top="-2px"
                                  right="-2px"
                                  width="75%"
                                  height="75%"
                                  color="black"
                                  asChild
                                >
                                  <BsGearFill />
                                </Icon>
                                <Icon
                                  position="absolute"
                                  top="-2px"
                                  right="-2px"
                                  width="65%"
                                  height="65%"
                                  color="white"
                                  asChild
                                >
                                  <BsGearFill />
                                </Icon>
                              </>
                            </Box>
                          }
                          label="正面を設定"
                          toolType={EDITOR_TOOLS.CAMERA_PROFILE}
                          buttonSize={EDITOR_TOOL_BUTTON_SIZE}
                          selectedTool={selectedTool}
                          changeTool={onChangeTool}
                          toolTipPlacement="top"
                        />
                      )}
                    </HStack>
                  </HoverCard.Content>
                </HoverCard.Content>
              </HoverCard.Positioner>
            </HoverCard.Root>
          </Box>
        </Box>
      ) : null}
    </HStack>
  )
}

export default CameraToolbar
